import React, { useMemo, useState } from 'react';
import {
  HelpCircle,
  X,
  ChevronLeft,
  Search,
  Mail,
  AlertCircle,
  BookOpen,
  ChevronRight,
  Lightbulb,
} from 'lucide-react';
import type { AppView } from '../types/views';
import {
  helpGuideCategories,
  getGuidesForContext,
  getGuidesByCategory,
  getGuideById,
  searchGuides,
  type HelpCategory,
  type HelpGuide,
} from '../data/helpGuides';
import { useHelpContext } from '../contexts/ToastContext';
import { BugReportModal } from './admin/BugReportModal';

interface HelpMenuProps {
  activeView: AppView;
  onViewChange: (view: AppView, context?: Record<string, unknown>) => void;
}

type PanelView =
  | { kind: 'home' }
  | { kind: 'guide'; guideId: string }
  | { kind: 'category'; category: HelpCategory };

function formatGuideText(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
}

function GuideDetail({
  guide,
  onBack,
  onOpenFaq,
}: {
  guide: HelpGuide;
  onBack: () => void;
  onOpenFaq: () => void;
}) {
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 line-clamp-2">{guide.title}</h3>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
            When to use this
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{guide.situation}</p>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Steps
          </p>
          <ol className="space-y-2">
            {guide.steps.map((step, index) => (
              <li key={index} className="flex gap-3 text-sm text-slate-700 dark:text-slate-300">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center">
                  {index + 1}
                </span>
                <span
                  className="leading-relaxed pt-0.5"
                  dangerouslySetInnerHTML={{ __html: formatGuideText(step) }}
                />
              </li>
            ))}
          </ol>
        </div>

        {guide.tips && guide.tips.length > 0 && (
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-950/30 p-3">
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 text-xs font-semibold uppercase tracking-wide mb-2">
              <Lightbulb className="w-4 h-4" />
              Tips
            </div>
            <ul className="space-y-1.5">
              {guide.tips.map((tip, index) => (
                <li key={index} className="text-sm text-amber-900 dark:text-amber-100/90 leading-relaxed">
                  • {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {guide.relatedFaqId && (
          <button
            type="button"
            onClick={onOpenFaq}
            className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            <BookOpen className="w-4 h-4" />
            More in Help &amp; FAQ
          </button>
        )}
      </div>
    </div>
  );
}

export function HelpMenu({ activeView, onViewChange }: HelpMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelView, setPanelView] = useState<PanelView>({ kind: 'home' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showBugReport, setShowBugReport] = useState(false);
  const { helpContext } = useHelpContext();

  const contextualGuides = useMemo(
    () => getGuidesForContext(activeView, helpContext, 5),
    [activeView, helpContext]
  );

  const hasContextMatch =
    Boolean(helpContext.surface || (helpContext.tags && helpContext.tags.length > 0)) ||
    activeView === 'bookings' ||
    activeView === 'clients' ||
    activeView === 'certificates' ||
    activeView === 'quotes' ||
    activeView === 'reminders' ||
    activeView === 'emails' ||
    activeView === 'reviews' ||
    activeView === 'ops';

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return null;
    return searchGuides(searchTerm);
  }, [searchTerm]);

  const openGuide = (guideId: string) => {
    setPanelView({ kind: 'guide', guideId });
    setSearchTerm('');
  };

  const closePanel = () => {
    setIsOpen(false);
    setPanelView({ kind: 'home' });
    setSearchTerm('');
  };

  const renderGuideButton = (guide: HelpGuide) => (
    <button
      key={guide.id}
      type="button"
      onClick={() => openGuide(guide.id)}
      className="flex w-full items-center justify-between gap-2 p-3 text-left bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-slate-700 transition-colors"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{guide.title}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">{guide.situation}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
    </button>
  );

  const selectedGuide = panelView.kind === 'guide' ? getGuideById(panelView.guideId) : undefined;
  const categoryGuides =
    panelView.kind === 'category' ? getGuidesByCategory(panelView.category) : [];

  return (
    <>
      <button
        onClick={() => (isOpen ? closePanel() : setIsOpen(true))}
        aria-label={isOpen ? 'Close help menu' : 'Open help menu'}
        className={`fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] lg:bottom-6 right-4 lg:right-6 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 ${
          isOpen
            ? 'bg-red-500 hover:bg-red-600'
            : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:shadow-xl'
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white mx-auto" />
        ) : (
          <HelpCircle className="w-6 h-6 text-white mx-auto" />
        )}
      </button>

      {isOpen && (
        <div className="fixed bottom-[calc(9.5rem+env(safe-area-inset-bottom))] lg:bottom-24 right-4 lg:right-6 z-50 w-96 max-w-[calc(100vw-2rem)] h-[min(520px,70dvh)] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Help</h3>
                <p className="text-xs text-blue-100">What to do in each situation</p>
              </div>
            </div>
          </div>

          {panelView.kind === 'guide' && selectedGuide ? (
            <GuideDetail
              guide={selectedGuide}
              onBack={() => setPanelView({ kind: 'home' })}
              onOpenFaq={() => {
                closePanel();
                onViewChange('faq');
              }}
            />
          ) : panelView.kind === 'category' ? (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
                <button
                  type="button"
                  onClick={() => setPanelView({ kind: 'home' })}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label="Back"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                </button>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{panelView.category}</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {categoryGuides.map(renderGuideButton)}
              </div>
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search guides..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-5">
                {searchResults ? (
                  searchResults.length > 0 ? (
                    <div className="space-y-2">{searchResults.map(renderGuideButton)}</div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-6">
                      No guides match &ldquo;{searchTerm}&rdquo;
                    </p>
                  )
                ) : (
                  <>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                        {hasContextMatch ? 'For this page' : 'Popular guides'}
                      </p>
                      <div className="space-y-2">{contextualGuides.map(renderGuideButton)}</div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                        Browse by area
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {helpGuideCategories.map((category) => {
                          const count = getGuidesByCategory(category).length;
                          if (count === 0) return null;
                          return (
                            <button
                              key={category}
                              type="button"
                              onClick={() => setPanelView({ kind: 'category', category })}
                              className="p-3 text-left rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
                            >
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{category}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{count} guides</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 shrink-0 flex flex-wrap gap-3">
            <a
              href="mailto:info@rapidcert.eu"
              className="inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Mail className="w-3.5 h-3.5" />
              Email support
            </a>
            <button
              type="button"
              onClick={() => setShowBugReport(true)}
              className="inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <AlertCircle className="w-3.5 h-3.5" />
              Report issue
            </button>
            <button
              type="button"
              onClick={() => {
                closePanel();
                onViewChange('faq');
              }}
              className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 hover:underline ml-auto"
            >
              <BookOpen className="w-3.5 h-3.5" />
              All FAQs
            </button>
          </div>
        </div>
      )}

      <BugReportModal isOpen={showBugReport} onClose={() => setShowBugReport(false)} />
    </>
  );
}
